import { formatCompact, formatFunding, formatPrice, formatSpreadBps } from "@/lib/formatters";
import { marketNotionalOi, marketSlug } from "@/lib/market-utils";
import type { Hip3Market } from "@/types/hip3";
import { useNavigate } from "react-router-dom";

type AssetTableProps = {
  markets: Hip3Market[];
  title?: string;
};

export function AssetTable({ markets, title = "Asset Markets" }: AssetTableProps) {
  const navigate = useNavigate();

  return (
    <section className="border border-border/80 bg-card/95">
      <div className="border-b border-border/80 px-3 py-2 text-xs font-mono uppercase tracking-wide text-muted-foreground">
        {title}
      </div>

      <div className="w-full overflow-x-auto">
        <table className="w-max min-w-full border-collapse">
          <thead className="text-[0.7em] lg:text-xs uppercase font-mono tracking-wide text-muted-foreground">
            <tr className="border-b border-border/70">
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-left">Provider</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-left">Market</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Price</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Open Interest</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Volume</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Funding</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Spread</th>
            </tr>
          </thead>
          <tbody>
            {!markets.length ? (
              <tr>
                <td colSpan={7} className="px-3 py-4 md:px-5 md:py-6 text-sm text-muted-foreground">
                  No markets available.
                </td>
              </tr>
            ) : (
              markets.map((market) => {
                const marketId = marketSlug(market);
                const marketPath = `/markets/${market.provider}/${marketId}`;
                return (
                  <tr
                    key={`${market.provider}:${market.marketId}`}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(marketPath)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(marketPath);
                      }
                    }}
                    className="cursor-pointer border-b border-border/60 text-xs md:text-sm hover:bg-primary/5"
                  >
                    <td className="p-3 lg:px-5 lg:py-4 text-sm font-semibold uppercase">
                      {market.provider}
                    </td>
                    <td className="p-3 lg:px-5 lg:py-4 text-sm">
                      {market.baseAsset} / {market.quoteAsset}
                    </td>
                    <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">${formatPrice(market.currentPrice)}</td>
                    <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">${formatCompact(marketNotionalOi(market))}</td>
                    <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">${formatCompact(market.volume24h)}</td>
                    <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">{formatFunding(market.funding)}</td>
                    <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">{formatSpreadBps(market.spreadBps)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
